<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 class="mb-0"><i class="fa fa-fw fa-arrow-left mr-2" />Daftar Retur</h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="No. Faktur Invoice">
                        <b-form-input v-model="filter.noFaktur" maxlength="50" @keydown.enter="getItems" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Customer">
                        <multiselect v-model="filter.customer"
                            label="name"
                            track-by="id"
                            :internal-search="true"
                            deselect-label=""
                            select-label=""
                            :options="optCustomer"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Gudang">
                        <multiselect v-model="filter.warehouse"
                            label="name"
                            track-by="id"
                            :internal-search="true"
                            deselect-label=""
                            select-label=""
                            :options="optWarehouse"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="3">
                    <b-form-group label="Tgl. Retur Dari">
                        <b-form-input type="date" v-model="filter.startDate" @keydown.enter="getItems" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Tgl. Retur Sampai">
                        <b-form-input type="date" v-model="filter.endDate" @keydown.enter="getItems" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Aksi">
                        <b-button variant="primary" @click="getItems()">
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-table bordered hover show-empty no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="onSortChanged"
                responsive class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data retur.
                </template>
                <template v-slot:cell(no_faktur)="row">
                    {{ row.item.invoice.no_faktur }}
                </template>
                <template v-slot:cell(refund)="row">
                    Rp. {{ $helper.formatThousand(row.item.refund) }}
                    <div class="small font-weight-bold">
                        {{ row.item.invoice.customer_name }}
                    </div>
                </template>
                <template v-slot:cell(warehouse_name)="row">
                    {{row.item.invoice.from_warehouse.name}}<br />
                    {{row.item.office_name}}
                </template>
                <template v-slot:cell(created_at)="row">
                    {{ $helper.parseDate(row.item.created) }}, {{ $helper.parseHourMinute(row.item.created) }}
                    <div class="small text-muted">
                        {{ row.item.created_by.fullname }}
                    </div>
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusBadge(row.item.status)[0]">{{ getStatusBadge(row.item.status)[1] }}</b-badge>
                </template>
                <template v-slot:cell(action)="row">
                    <b-button variant="primary" size="sm" v-b-tooltip.hover.top title="List Barang Retur" @click="displayModalItems(row.item.id)">
                        <i class="fa fa-fw fa-list" />
                    </b-button>                    
                    <b-button v-if="row.item.status == 1 && allowedRetur" size="sm" class="ml-2" variant="success" v-b-tooltip.hover.top title="Terima" @click="showModalPin(row.item)">
                        <i class="fa fa-fw fa-check" />
                    </b-button>
                    <b-button v-if="row.item.status == 1 && allowedRetur" size="sm" class="ml-2" variant="danger" v-b-tooltip.hover.top title="Tolak" @click="rejectRequest(row.item)">
                        <i class="fa fa-fw fa-times" />
                    </b-button>
                </template>
            </b-table>
            <PageSizer :totalRows="totalRows" :page="currentPage" :options="options" @limit-change="onLimitChange" @page-change="onPageChange" />
        </b-card>
        <b-modal id="m-reprint-code" title="Reprint" centered no-close-on-backdrop hide-footer @shown="$refs.passcode.focus()">
            <b-form-group label="Passcode Reprint">
                <b-form-input type="password" ref="passcode" maxlength="10" v-model="passcode" @keydown.enter="getReprintBarcode" />
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button variant="primary" :disabled="isSubmitting" @click="getReprintBarcode">
                    {{ isSubmitting ? "Mengambil..." : "Reprint" }}
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-reprint-barcode" title="Reprint Barcode" no-close-on-backdrop no-close-on-esc hide-footer hide-header-close>
            Silahkan cetak invoice dibawah ini. Mohon pastikan invoice tercetak dengan baik sebelum melanjutkan.
            <br /><br />
            <iframe :src="$BASE_PRINT_FILE_PATH + getInvoiceLink()" height="300px" width="100%" />
            <div class="mt-3">
                <b-row>
                    <b-col md="6">
                        <b-button variant="dark" :disabled="invoiceSizeCooldown" @click="toggleInvoiceSize">
                            Print {{ invoiceSize == 'A5' ? 'A4' : 'A5' }}
                        </b-button>
                    </b-col>
                    <b-col md="6" class="text-right">
                        <b-button variant="primary" @click="$bvModal.hide('m-reprint-barcode')">
                            Tutup
                        </b-button>
                    </b-col>
                </b-row>
            </div>
        </b-modal>
        <b-modal id="m-items" title="Barang Retur" size="lg" hide-footer no-close-on-backdrop no-close-on-esc>
            <ModalItemReturRequest :returnRequestId="activeId" />
        </b-modal>
        <b-modal id="m-pin" title="PIN Otorisasi" centered hide-footer>
            <b-form-group label="Masukan PIN">
                <b-input type="password" v-model="pin" maxlength="10" @keydown.enter="checkPIN" autofocus />
            </b-form-group>
            <div class="mt-4 text-right">
                <b-button variant="primary" :disabled="isSubmitting" @click="checkPIN">
                    Otorisasi
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-retur-success" title="Retur Barang Berhasil" centered hide-footer no-close-on-backdrop no-close-on-esc hide-header-close>
            Retur berhasil disimpan, dengan perincian pembayaran sebagai berikut:
            <ul class="mt-2">
                <li v-for="item in returResult.invoices" v-bind:key="item.no_faktur">
                    {{ item.no_faktur }} - <strong>Rp. {{ $helper.formatThousand(item.amount) }} <span v-if="item.is_settled == true">[LUNAS]</span></strong>
                </li>
                <li v-if="returResult.debt != 0">
                    Sebagai hutang ke customer / deposit - <strong>Rp. {{ $helper.formatThousand(returResult.debt) }}</strong>
                </li>
            </ul>
            <div class="mt-4">
                Tekan bagian "Sinar Mutiara Cell" untuk mencetak faktur retur.
                <iframe :src="$BASE_PRINT_FILE_PATH + getReturnInvoiceLink()" height="300px" width="100%" />
            </div>
            <div class="mt-4">
                <b-row>
                    <b-col md="6">
                        <b-button variant="dark" :disabled="returnInvoiceSizeCooldown" @click="toggleReturnInvoiceSize">
                            Print {{ returnInvoiceSize == 'A5' ? 'A4' : 'A5' }}
                        </b-button>
                    </b-col>
                    <b-col md="6" class="text-right">
                        <b-button variant="primary" @click="$bvModal.hide('m-retur-success')">
                            OK
                        </b-button>
                    </b-col>
                </b-row>
            </div>
        </b-modal>
    </div>
</template>

<script>
import ModalItemReturRequest from './ModalItemReturRequest.vue'
export default {
    components: {
        ModalItemReturRequest
    },
    data() {
        return {

            initStatus: 0,
            isBusy: true,
            isSubmitting: false,
            show: 10,

            activeId: 0,
            passcode: "",
            link: "",
            linkA4: "",

            filter: {
                noFaktur: "",
                startDate: "",
                endDate: "",
                customer: null,
                warehouse: null
            },

            options: [
                { value: 10, text: '10'},
                { value: 20, text: '20'},
                { value: 30, text: '30'},
                { value: 40, text: '40'},
                { value: 50, text: '50'},
            ],

            fields: [
                { key: "no_faktur", label: "No. Faktur Invoice", sortable: true },
                { key: "refund", label: "Refund", sortable: true },
                { key: "amount", label: "Jumlah Barang", sortable: true },
                { key: "warehouse_name", label: "Gudang", sortable: true },
                { key: "created_at", label: "Retur pada / oleh", sortable: true },
                { key: "status", label: "Status", sortable: true },
                { key: "action", label: "Aksi" },
            ],

            items: [],
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,

            optCustomer: [],
            optWarehouse: [],
            activeItem: null,
            pin: "",
            returResult: {},

            allowedRetur: false,

            invoiceSize: 'A5',
            invoiceSizeCooldown: false,

            returnInvoiceSize: 'A5',
            returnInvoiceSizeCooldown: false,

        }
    },

    methods: {

        init() {

            this.initStatus = 0
            let getItems = this.getItems()

            let getCustomers = this.$axios.get("customer/selection").then(res => {
                this.optCustomer = res.data.data
            })

            let getWarehouses = this.$axios.get("master/warehouse/selection").then(res => {
                res.data.data.map(item => {
                    this.optWarehouse.push({
                        id: item.id,
                        name: item.name + " - " + item.office_name
                    })
                })
            })

            let checkPermission = this.$axios.get("return-request/check-permission").then(res => {
                this.allowedRetur = res.data.data
            })

            Promise.all([getItems, getCustomers, getWarehouses, checkPermission]).then(() => {
                this.initStatus = 1
            })
            .catch(error => {
                this.initStatus = -1
            })

        },

        getItems(page = 1) {

            this.isBusy = true
            this.items = []

            if(isNaN(page)) {
                this.currentPage = 1
            }
            else {
                this.currentPage = page
            }

            // URL builder
            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage)

            if(this.filter.customer != null) {
                param += "&filterCustomer=" + this.filter.customer.id
            }

            if(this.filter.warehouse != null) {
                param += "&filterWarehouse=" + this.filter.warehouse.id
            }

            if(!this.$helper.isBlank(this.filter.noFaktur)) {
                param += "&filterNoFaktur=" + this.filter.noFaktur
            }

            if(!this.$helper.isBlank(this.filter.startDate)) {
                param += "&filterStartDate=" + this.filter.startDate
            }

            if(!this.$helper.isBlank(this.filter.endDate)) {
                param += "&filterEndDate=" + this.filter.endDate
            }

            if(this.sortBy != null) {
                param += "&sortBy=" + this.sortBy + "&sortDesc=" + this.sortDesc
            }

            return this.$axios.get("return-request" + param).then(res => {
                this.items = res.data.data.items
                this.totalRows = res.data.data.total
                sessionStorage.setItem("listReturRequestFilter", JSON.stringify(this.filter))
            })
            .catch(error => {
                this.$helper.parseError(this, error)
            })
            .finally(() => {
                this.isBusy = false
            })

        },

        prepareReprint(id) {
            this.activeId = id
            this.$bvModal.show("m-reprint-code")
        },

        getReprintBarcode() {

            this.isSubmitting = true
            let payload = {
                id: this.activeId,
                passcode: this.passcode
            }

            this.$axios.post("returns/reprint", payload).then(res => {

                this.link = res.data.data.link
                this.linkA4 = res.data.data.linka4
                this.$bvModal.hide("m-reprint-code")
                this.$bvModal.show("m-reprint-barcode")
                this.passcode = ""

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        acceptRequest() {

            this.isSubmitting = true
            let payload = {
                returnRequestId: this.activeItem.id
            }

            this.$axios.post("return-request/approve", payload).then(res => {

                this.returResult = res.data.data
                this.$bvModal.show("m-retur-success")

                this.$helper.toastSuccess(this, "Request berhasil diapprove!")
                this.getItems(this.currentPage)

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        rejectRequest(item){

            this.activeItem = item

            let isConfirmed = confirm(`Tolak request retur no faktur ${item.invoice.no_faktur}?`)
            if(!isConfirmed) return

            this.isSubmitting = true
            let payload = {
                returnRequestId: this.activeItem.id
            }

            this.$axios.post("return-request/reject", payload).then(_ => {

                this.$helper.toastSuccess(this, "Request berhasil ditolak!")
                this.getItems(this.currentPage)

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        checkPIN() {

            if(this.pin == "") return this.$helper.toastError(this, "PIN kosong!")

            let payload = {
                pin: this.pin
            }

            this.isSubmitting = true
            this.$axios.post("invoice/check-pin", payload).then(_ => {
                this.pin = ""
                this.$bvModal.hide("m-pin")
                this.acceptRequest()
            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        showModalPin(item){

            let isConfirmed = confirm(`Lakukan approval untuk request retur no faktur ${item.invoice.no_faktur}?`)
            if(!isConfirmed) return

            this.activeItem = item
            this.$bvModal.show("m-pin")

        },

        displayModalItems(id) {
            this.activeId = id
            this.$bvModal.show("m-items")
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage
            this.getItems(this.currentPage)
        },

        onPageChange(newPage) {
            this.currentPage = newPage
            this.getItems(this.currentPage)
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy
            this.sortDesc = ctx.sortDesc
            this.getItems()
        },

        getStatusBadge(status) {
            switch(status) {
                case 1: return ["warning", "PENDING"]
                case 2: return ["success", "DITERIMA"]
                case 3: return ["danger", "DITOLAK"]
                default: return ["light", "-"]
            }
        },

        getInvoiceLink() {
            if(this.invoiceSize == 'A5'){
                return this.link
            }
            else{
                return this.linkA4
            }
        },

        toggleInvoiceSize() {
            this.invoiceSizeCooldown = true
            if(this.invoiceSize == 'A5') this.invoiceSize = 'A4'
            else this.invoiceSize = 'A5'
            setTimeout(function(){
                this.invoiceSizeCooldown = false
            }.bind(this),2000);
        },

        getReturnInvoiceLink() {
            if(this.returnInvoiceSize == 'A5'){
                return this.returResult.link
            }
            else{
                return this.returResult.linka4
            }
        },

        toggleReturnInvoiceSize() {
            this.returnInvoiceSizeCooldown = true
            if(this.returnInvoiceSize == 'A5') this.returnInvoiceSize = 'A4'
            else this.returnInvoiceSize = 'A5'
            setTimeout(function(){
                this.returnInvoiceSizeCooldown = false
            }.bind(this),2000);
        },

    },

    created() {
        if(!this.$helper.isBlank(sessionStorage.getItem("listReturRequestFilter"))){
            this.filter = JSON.parse(sessionStorage.getItem("listReturRequestFilter"))
        }
        this.init()

        console.log(this.allowedRetur)
    }
}
</script>
